import type {
  CodexEntry,
  Encounter,
  MasteryDimension,
  Region,
  Skill,
} from '../types/domain';
import { assetMap } from './assetMap';
import { assertValidContent } from './contentValidation';

const allMasteryDimensions: MasteryDimension[] = [
  'recognition',
  'application',
  'justification',
  'reproduction',
  'transfer',
];

type SkillSeed = Omit<Skill, 'unlocks' | 'masteryDimensions'> & {
  masteryDimensions?: MasteryDimension[];
};

function campaignSkill(
  id: string,
  title: string,
  type: Skill['type'],
  regionId: string,
  formalStatement: string,
  prerequisites: string[],
  tags: string[],
): SkillSeed {
  const analyticalRegion = ['cartesian-plane', 'distances', 'centers', 'analytic-proofs', 'line-forge'].includes(regionId);
  return {
    id,
    title,
    shortTitle: title,
    description: formalStatement,
    type,
    regionId,
    formalStatement,
    prerequisites,
    tags,
    assetKey: regionId === 'angle-temple' ? 'angles-core' : regionId === 'cevians-sanctuary' ? 'cevians-core' : 'congruence-cases',
    codexEntryId: `codex-${id}`,
    sourceRefs: [{
      origin: analyticalRegion ? 'Lista Analítica' : 'Lista Euclidiana',
      reference: analyticalRegion ? 'Campanha Analítica · Listas 1 e 2' : 'Campanha Euclidiana · Listas 1 e 2',
    }],
    visibility: 'hiddenUntilDiscovered',
  };
}

const skillSeeds: SkillSeed[] = [
  {
    id: 'segments',
    title: 'Segmentos de reta',
    shortTitle: 'Segmentos',
    description: 'Identifique extremidades e compare comprimentos sem depender da aparência.',
    type: 'definition',
    regionId: 'foundations',
    formalStatement: 'Um segmento de reta é a parte da reta limitada por dois pontos distintos.',
    prerequisites: [],
    tags: ['segmento', 'comprimento', 'fundamentos'],
    assetKey: 'congruence-core',
    codexEntryId: 'codex-segments',
    sourceRefs: [{ origin: 'Aula', reference: 'Fundamentos: pontos, retas e segmentos' }],
    visibility: 'visibleButLocked',
  },
  {
    id: 'midpoint',
    title: 'Ponto médio',
    shortTitle: 'Ponto médio',
    description: 'Reconheça e justifique a divisão de um segmento em duas partes congruentes.',
    type: 'definition',
    regionId: 'foundations',
    formalStatement: 'O ponto médio de um segmento pertence ao segmento e o divide em dois segmentos congruentes.',
    prerequisites: ['segments'],
    tags: ['segmento', 'congruência', 'divisão'],
    assetKey: 'congruence-core',
    codexEntryId: 'codex-midpoint',
    sourceRefs: [{ origin: 'Aula', reference: 'Fundamentos: ponto médio' }],
    visibility: 'visibleButLocked',
  },
  {
    id: 'angles',
    title: 'Ângulos',
    shortTitle: 'Ângulos',
    description: 'Leia vértice, lados e medida de um ângulo como objetos matemáticos.',
    type: 'definition',
    regionId: 'angle-temple',
    formalStatement: 'Um ângulo é a figura formada por duas semirretas de mesma origem.',
    prerequisites: [],
    tags: ['ângulo', 'semirreta', 'vértice'],
    assetKey: 'angles-core',
    codexEntryId: 'codex-angles',
    sourceRefs: [{ origin: 'Aula', reference: 'Ângulos: definição e notação' }],
    visibility: 'visibleButLocked',
  },
  {
    id: 'complementary-angles',
    title: 'Ângulos complementares',
    shortTitle: 'Complementares',
    description: 'Relacione dois ângulos cuja soma é um ângulo reto.',
    type: 'definition',
    regionId: 'angle-temple',
    formalStatement: 'Dois ângulos são complementares quando a soma de suas medidas é 90°.',
    prerequisites: ['angles'],
    tags: ['ângulo', 'soma', '90 graus'],
    assetKey: 'angles-core',
    codexEntryId: 'codex-complementary-angles',
    sourceRefs: [{ origin: 'Lista Euclidiana', reference: 'Relações angulares' }],
    visibility: 'visibleButLocked',
  },
  {
    id: 'supplementary-angles',
    title: 'Ângulos suplementares',
    shortTitle: 'Suplementares',
    description: 'Use pares lineares para construir argumentos de soma angular.',
    type: 'definition',
    regionId: 'angle-temple',
    formalStatement: 'Dois ângulos são suplementares quando a soma de suas medidas é 180°.',
    prerequisites: ['angles'],
    tags: ['ângulo', 'soma', '180 graus'],
    assetKey: 'angles-core',
    codexEntryId: 'codex-supplementary-angles',
    sourceRefs: [{ origin: 'Lista Euclidiana', reference: 'Relações angulares' }],
    visibility: 'visibleButLocked',
  },
  {
    id: 'opv',
    title: 'Ângulos opostos pelo vértice',
    shortTitle: 'OPV',
    description: 'Reconheça ângulos formados por retas concorrentes e prove sua igualdade.',
    type: 'theorem',
    regionId: 'angle-temple',
    formalStatement: 'Se duas retas se intersectam, então os ângulos opostos pelo vértice são congruentes.',
    prerequisites: ['angles'],
    tags: ['ângulo', 'retas concorrentes', 'igualdade'],
    assetKey: 'angles-core',
    codexEntryId: 'codex-opv',
    sourceRefs: [{ origin: 'Lista Euclidiana', reference: 'Ângulos opostos pelo vértice' }],
    visibility: 'visibleButLocked',
  },
  {
    id: 'angle-bisector',
    title: 'Bissetriz de um ângulo',
    shortTitle: 'Bissetriz',
    description: 'Construa a semirreta que divide um ângulo em duas partes congruentes.',
    type: 'definition',
    regionId: 'angle-temple',
    formalStatement: 'A bissetriz de um ângulo é a semirreta interna que o divide em dois ângulos congruentes.',
    prerequisites: ['angles'],
    tags: ['ângulo', 'construção', 'congruência'],
    assetKey: 'angles-core',
    codexEntryId: 'codex-angle-bisector',
    sourceRefs: [{ origin: 'Aula', reference: 'Ângulos: bissetriz' }],
    visibility: 'visibleButLocked',
  },
  {
    id: 'triangles',
    title: 'Triângulos',
    shortTitle: 'Triângulos',
    description: 'Leia lados, vértices e ângulos na ordem exigida por uma afirmação.',
    type: 'definition',
    regionId: 'triangles',
    formalStatement: 'Um triângulo é determinado por três pontos não colineares e pelos segmentos que os unem.',
    prerequisites: [],
    tags: ['triângulo', 'lados', 'vértices'],
    assetKey: 'congruence-core',
    codexEntryId: 'codex-triangles',
    sourceRefs: [{ origin: 'Aula', reference: 'Triângulos: elementos e notação' }],
    visibility: 'visibleButLocked',
  },
  {
    id: 'triangle-congruence',
    title: 'Congruência de triângulos',
    shortTitle: 'Congruência',
    description: 'Associe vértices correspondentes e preserve todas as medidas.',
    type: 'definition',
    regionId: 'congruence-fortress',
    formalStatement: 'Triângulos congruentes admitem uma correspondência que preserva lados e ângulos.',
    prerequisites: ['triangles'],
    tags: ['triângulo', 'correspondência', 'congruência'],
    assetKey: 'congruence-core',
    codexEntryId: 'codex-triangle-congruence',
    sourceRefs: [{ origin: 'Aula', reference: 'Congruência de triângulos' }],
    visibility: 'visibleButLocked',
  },
  {
    id: 'sas',
    title: 'Caso Lado–Ângulo–Lado',
    shortTitle: 'LAL',
    description: 'Prove congruência usando dois lados e o ângulo compreendido.',
    type: 'postulate',
    regionId: 'congruence-fortress',
    formalStatement: 'Dois lados e o ângulo compreendido congruentes determinam triângulos congruentes.',
    prerequisites: ['triangle-congruence'],
    tags: ['triângulo', 'congruência', 'LAL'],
    assetKey: 'congruence-cases',
    codexEntryId: 'codex-sas',
    sourceRefs: [{ origin: 'Lista Euclidiana', reference: 'Casos de congruência: LAL' }],
    visibility: 'hiddenUntilDiscovered',
  },
  {
    id: 'isosceles-theorem',
    title: 'Teorema do triângulo isósceles',
    shortTitle: 'Isósceles',
    description: 'Derive a igualdade dos ângulos da base a partir dos lados congruentes.',
    type: 'theorem',
    regionId: 'congruence-fortress',
    formalStatement: 'Em um triângulo isósceles, os ângulos opostos aos lados congruentes são congruentes.',
    prerequisites: ['sas'],
    tags: ['triângulo', 'isósceles', 'ângulos da base'],
    assetKey: 'isosceles-core',
    codexEntryId: 'codex-isosceles-theorem',
    sourceRefs: [{ origin: 'Lista Euclidiana', reference: 'Teorema do triângulo isósceles' }],
    visibility: 'hiddenUntilDiscovered',
  },
  {
    id: 'asa',
    title: 'Caso Ângulo–Lado–Ângulo',
    shortTitle: 'ALA',
    description: 'Use dois ângulos e o lado compreendido para provar congruência.',
    type: 'theorem',
    regionId: 'congruence-fortress',
    formalStatement: 'Dois ângulos e o lado compreendido congruentes determinam triângulos congruentes.',
    prerequisites: ['sas', 'isosceles-theorem'],
    tags: ['triângulo', 'congruência', 'ALA'],
    assetKey: 'congruence-cases',
    codexEntryId: 'codex-asa',
    sourceRefs: [{ origin: 'Complemento', reference: 'Derivação do caso ALA' }],
    visibility: 'hiddenUntilDiscovered',
  },
  {
    id: 'isosceles-converse',
    title: 'Conversa do teorema do isósceles',
    shortTitle: 'Conversa',
    description: 'Conclua igualdade de lados a partir de ângulos da base congruentes.',
    type: 'theorem',
    regionId: 'congruence-fortress',
    formalStatement: 'Se dois ângulos de um triângulo são congruentes, então os lados opostos a eles são congruentes.',
    prerequisites: ['asa'],
    tags: ['triângulo', 'isósceles', 'conversa'],
    assetKey: 'isosceles-core',
    codexEntryId: 'codex-isosceles-converse',
    sourceRefs: [{ origin: 'Lista Euclidiana', reference: 'Conversa do teorema do isósceles' }],
    visibility: 'hiddenUntilDiscovered',
  },
  {
    id: 'sss',
    title: 'Caso Lado–Lado–Lado',
    shortTitle: 'LLL',
    description: 'Transforme três igualdades de lados em uma prova de congruência.',
    type: 'theorem',
    regionId: 'congruence-fortress',
    formalStatement: 'Três pares de lados correspondentes congruentes determinam triângulos congruentes.',
    prerequisites: ['asa', 'isosceles-converse'],
    tags: ['triângulo', 'congruência', 'LLL'],
    assetKey: 'congruence-cases',
    codexEntryId: 'codex-sss',
    sourceRefs: [{ origin: 'Complemento', reference: 'Derivação do caso LLL' }],
    visibility: 'hiddenUntilDiscovered',
  },
  {
    id: 'median',
    title: 'Mediana',
    shortTitle: 'Mediana',
    description: 'Ligue um vértice ao ponto médio do lado oposto.',
    type: 'definition',
    regionId: 'cevians-sanctuary',
    formalStatement: 'Mediana é o segmento que liga um vértice ao ponto médio do lado oposto.',
    prerequisites: ['midpoint', 'triangles'],
    tags: ['triângulo', 'ceviana', 'ponto médio'],
    assetKey: 'cevians-core',
    codexEntryId: 'codex-median',
    sourceRefs: [{ origin: 'Aula', reference: 'Cevianas: mediana' }],
    visibility: 'visibleButLocked',
  },
  {
    id: 'altitude',
    title: 'Altura',
    shortTitle: 'Altura',
    description: 'Construa a perpendicular de um vértice à reta do lado oposto.',
    type: 'definition',
    regionId: 'cevians-sanctuary',
    formalStatement: 'Altura é o segmento perpendicular traçado de um vértice à reta que contém o lado oposto.',
    prerequisites: ['triangles'],
    tags: ['triângulo', 'ceviana', 'perpendicular'],
    assetKey: 'cevians-core',
    codexEntryId: 'codex-altitude',
    sourceRefs: [{ origin: 'Aula', reference: 'Cevianas: altura' }],
    visibility: 'visibleButLocked',
  },
  {
    id: 'cevians',
    title: 'Cevianas',
    shortTitle: 'Cevianas',
    description: 'Diferencie mediana, bissetriz e altura pelas propriedades que as definem.',
    type: 'definition',
    regionId: 'cevians-sanctuary',
    formalStatement: 'Ceviana é um segmento com uma extremidade em um vértice e outra no lado oposto ou em sua reta suporte.',
    prerequisites: ['median', 'angle-bisector', 'altitude'],
    tags: ['triângulo', 'mediana', 'bissetriz', 'altura'],
    assetKey: 'cevians-core',
    codexEntryId: 'codex-cevians',
    sourceRefs: [{ origin: 'Lista Euclidiana', reference: 'Cevianas e suas propriedades' }],
    visibility: 'hiddenUntilDiscovered',
  },
  {
    id: 'isosceles-special-cevian',
    title: 'Ceviana especial do isósceles',
    shortTitle: 'Tríplice ceviana',
    description: 'Prove quando a bissetriz também é mediana e altura.',
    type: 'corollary',
    regionId: 'cevians-sanctuary',
    formalStatement: 'No triângulo isósceles, a bissetriz do ângulo do vértice também é mediana e altura da base.',
    prerequisites: ['isosceles-theorem', 'angle-bisector', 'sas'],
    tags: ['isósceles', 'bissetriz', 'mediana', 'altura'],
    assetKey: 'isosceles-core',
    codexEntryId: 'codex-isosceles-special-cevian',
    sourceRefs: [{ origin: 'Lista Euclidiana', reference: 'Cevianas no triângulo isósceles' }],
    visibility: 'hiddenUntilDiscovered',
  },
  campaignSkill('right-angle', 'Ângulo reto', 'definition', 'angle-temple', 'Ângulo reto é o ângulo de medida 90°.', ['angles'], ['ângulo', '90 graus']),
  campaignSkill('straight-angle', 'Ângulo raso', 'definition', 'angle-temple', 'Ângulo raso é o ângulo de medida 180°.', ['angles'], ['ângulo', '180 graus']),
  campaignSkill('adjacent-angles', 'Ângulos adjacentes', 'definition', 'angle-temple', 'Ângulos adjacentes compartilham vértice e um lado, sem sobreposição interior.', ['angles'], ['ângulo', 'adjacência']),
  campaignSkill('angle-algebra', 'Linguagem algébrica angular', 'algebra', 'angle-temple', 'Relações angulares podem ser traduzidas em equações preservando suas justificativas geométricas.', ['complementary-angles', 'supplementary-angles'], ['ângulo', 'álgebra']),
  campaignSkill('parallel-angle-families', 'Ângulos em paralelas', 'theorem', 'parallel-pass', 'Retas paralelas cortadas por uma transversal determinam pares correspondentes e alternos congruentes e pares colaterais suplementares.', ['angles'], ['paralelismo', 'transversal', 'ângulos']),
  campaignSkill('parallel-converse-skill', 'Conversas de paralelismo', 'theorem', 'parallel-pass', 'A ocorrência das relações angulares adequadas permite concluir que duas retas são paralelas.', ['parallel-angle-families'], ['paralelismo', 'conversa', 'prova']),
  campaignSkill('parallelogram-characterization', 'Caracterização por diagonais', 'theorem', 'quadrilaterals-city', 'Um quadrilátero é paralelogramo se e somente se suas diagonais se cortam nos respectivos pontos médios.', ['parallel-converse-skill', 'midpoint', 'sas'], ['paralelogramo', 'diagonais', 'ponto médio']),
  campaignSkill('equilateral-triangle', 'Triângulo equilátero', 'definition', 'triangles', 'Triângulo equilátero possui três lados congruentes.', ['triangles'], ['triângulo', 'equilátero']),
  campaignSkill('scalene-triangle', 'Triângulo escaleno', 'definition', 'triangles', 'Triângulo escaleno não possui lados congruentes entre si.', ['triangles'], ['triângulo', 'escaleno']),
  campaignSkill('triangle-perimeter', 'Perímetro triangular', 'algebra', 'triangles', 'O perímetro de um triângulo é a soma das medidas de seus três lados.', ['triangles', 'segments'], ['triângulo', 'perímetro']),
  campaignSkill('cpctc', 'Partes correspondentes', 'property', 'congruence-fortress', 'Partes correspondentes de triângulos congruentes são congruentes.', ['triangle-congruence'], ['congruência', 'CPCTC']),
  campaignSkill('reflexivity', 'Reflexividade', 'logic', 'congruence-fortress', 'Todo objeto geométrico é congruente ou igual a si mesmo.', ['triangle-congruence'], ['lógica', 'reflexividade']),
  campaignSkill('equiangular-equilateral', 'Equiângulo implica equilátero', 'corollary', 'congruence-fortress', 'Se os três ângulos de um triângulo são congruentes, então seus três lados são congruentes.', ['isosceles-converse'], ['triângulo', 'converso']),
  campaignSkill('iff-logic', 'Se e somente se', 'logic', 'congruence-fortress', 'Uma equivalência exige provar uma implicação e sua conversa.', ['isosceles-converse'], ['lógica', 'equivalência']),
  campaignSkill('triangulation', 'Triangulação', 'technique', 'symmetries', 'Uma diagonal pode decompor um polígono em triângulos para transferir resultados de congruência.', ['sss'], ['construção', 'triangulação']),
  campaignSkill('perpendicular-bisector', 'Mediatriz', 'definition', 'symmetries', 'A mediatriz de um segmento é a reta perpendicular que passa por seu ponto médio.', ['midpoint', 'altitude'], ['mediatriz', 'perpendicular']),
  campaignSkill('equidistance', 'Equidistância', 'theorem', 'symmetries', 'Um ponto pertence à mediatriz de um segmento se e somente se é equidistante de suas extremidades.', ['perpendicular-bisector', 'sas'], ['mediatriz', 'distância']),
  campaignSkill('auxiliary-construction', 'Construção auxiliar', 'technique', 'symmetries', 'Uma construção auxiliar introduz objetos que revelam relações sem alterar as hipóteses.', ['sas'], ['construção', 'prova']),
  campaignSkill('diagonals', 'Diagonais como ação', 'technique', 'symmetries', 'Traçar diagonais adequadas pode produzir pares de triângulos comparáveis.', ['triangulation'], ['diagonal', 'construção']),
  campaignSkill('saccheri', 'Quadrilátero de Saccheri', 'theorem', 'symmetries', 'Em um quadrilátero de Saccheri, relações de perpendicularidade e congruência geram simetria entre os lados e ângulos do topo.', ['diagonals', 'sss'], ['Saccheri', 'rigidez']),
  campaignSkill('interior-angle-sum', 'Soma interna do triângulo', 'theorem', 'inequalities', 'A soma das medidas dos ângulos internos de um triângulo é 180°.', ['triangles', 'supplementary-angles'], ['triângulo', 'soma angular']),
  campaignSkill('exterior-angle-theorem', 'Teorema do ângulo externo', 'theorem', 'inequalities', 'A medida de um ângulo externo é a soma das medidas dos dois ângulos internos não adjacentes.', ['interior-angle-sum'], ['ângulo externo', 'triângulo']),
  campaignSkill('triangle-inequality', 'Desigualdade triangular', 'theorem', 'inequalities', 'Em um triângulo, a medida de cada lado é menor que a soma das medidas dos outros dois.', ['triangles', 'auxiliary-construction'], ['triângulo', 'desigualdade']),
  campaignSkill('angle-side-comparison', 'Comparação lado–ângulo', 'theorem', 'inequalities', 'Em um triângulo, o maior lado se opõe ao maior ângulo e reciprocamente.', ['isosceles-converse', 'triangle-inequality'], ['triângulo', 'comparação']),
  campaignSkill('reflection', 'Reflexão geométrica', 'technique', 'symmetries', 'Refletir uma figura preserva distâncias e ângulos e pode retificar caminhos quebrados.', ['perpendicular-bisector'], ['reflexão', 'simetria']),
  campaignSkill('optimization', 'Otimização geométrica', 'technique', 'symmetries', 'Problemas de menor caminho podem ser transformados por isometrias em comparações retilíneas.', ['reflection', 'triangle-inequality'], ['otimização', 'distância']),
  campaignSkill('contraposition', 'Contraposição', 'logic', 'cevians-sanctuary', 'A implicação P⇒Q pode ser provada pela implicação equivalente não-Q⇒não-P.', ['iff-logic'], ['lógica', 'contraposição']),
  campaignSkill('equilateral-properties', 'Propriedades do equilátero', 'corollary', 'cevians-sanctuary', 'No triângulo equilátero, medianas, bissetrizes e alturas coincidem em cada vértice.', ['equilateral-triangle', 'isosceles-special-cevian'], ['equilátero', 'cevianas']),
  campaignSkill('cartesian-coordinates', 'Coordenadas cartesianas', 'definition', 'cartesian-plane', 'Um ponto do plano é representado por um par ordenado cuja posição codifica deslocamentos nos eixos.', [], ['coordenadas', 'plano']),
  campaignSkill('quadrants-signs', 'Quadrantes e sinais', 'property', 'cartesian-plane', 'Os sinais de x e y determinam o quadrante de um ponto fora dos eixos.', ['cartesian-coordinates'], ['quadrante', 'sinais']),
  campaignSkill('diagonal-lines', 'Retas y=x e y=-x', 'property', 'cartesian-plane', 'As retas y=x e y=-x separam o plano por relações de igualdade e oposição entre coordenadas.', ['cartesian-coordinates'], ['reta', 'simetria']),
  campaignSkill('distance-formula-skill', 'Distância no plano', 'theorem', 'distances', 'A distância entre dois pontos é a raiz quadrada da soma dos quadrados das variações coordenadas.', ['cartesian-coordinates'], ['distância', 'Pitágoras']),
  campaignSkill('squared-distance', 'Distância ao quadrado', 'technique', 'distances', 'Comparações métricas podem ser feitas sem radicais usando o quadrado da distância.', ['distance-formula-skill'], ['distância', 'quadrado']),
  campaignSkill('coordinate-equidistance', 'Equidistância coordenada', 'theorem', 'distances', 'A igualdade de distâncias a dois pontos produz uma equação de lugar geométrico.', ['squared-distance'], ['equidistância', 'lugar geométrico']),
  campaignSkill('coordinate-midpoint', 'Ponto médio por coordenadas', 'theorem', 'cartesian-plane', 'As coordenadas do ponto médio são as médias aritméticas das coordenadas das extremidades.', ['cartesian-coordinates', 'midpoint'], ['ponto médio', 'média']),
  campaignSkill('segment-division', 'Divisão de segmento', 'algebra', 'cartesian-plane', 'Razões internas de divisão produzem combinações ponderadas das coordenadas das extremidades.', ['coordinate-midpoint'], ['segmento', 'razão']),
  campaignSkill('coordinate-median', 'Mediana por coordenadas', 'technique', 'cartesian-plane', 'A mediana pode ser representada ligando um vértice ao ponto médio coordenado do lado oposto.', ['coordinate-midpoint', 'median'], ['mediana', 'coordenadas']),
  campaignSkill('inverse-coordinate-system', 'Sistema coordenado inverso', 'algebra', 'cartesian-plane', 'Conhecer ponto médio ou razão e uma extremidade permite recuperar a extremidade desconhecida por sistema linear.', ['segment-division'], ['sistema', 'coordenadas']),
  campaignSkill('centroid-coordinate', 'Baricentro por coordenadas', 'theorem', 'centers', 'As coordenadas do baricentro são as médias aritméticas das coordenadas dos três vértices.', ['coordinate-median'], ['baricentro', 'média']),
  campaignSkill('axis-reflection', 'Reflexão nos eixos', 'technique', 'symmetries', 'Refletir nos eixos altera o sinal da coordenada perpendicular ao eixo e preserva a outra.', ['cartesian-coordinates'], ['reflexão', 'eixos']),
  campaignSkill('central-symmetry', 'Simetria central', 'technique', 'symmetries', 'A simetria central em um ponto envia cada ponto ao oposto em relação ao centro.', ['coordinate-midpoint'], ['simetria', 'origem']),
  campaignSkill('symmetry-composition', 'Composição de simetrias', 'technique', 'symmetries', 'Compor reflexões pode produzir rotação, translação ou simetria central.', ['axis-reflection', 'central-symmetry'], ['simetria', 'composição']),
  campaignSkill('coordinate-collinearity', 'Colinearidade coordenada', 'algebra', 'analytic-proofs', 'Três pontos são colineares quando satisfazem uma mesma relação linear, equivalentes por determinante ou inclinação compatível.', ['cartesian-coordinates'], ['colinearidade', 'reta']),
  campaignSkill('general-line-equation', 'Equação geral da reta', 'algebra', 'line-forge', 'A colinearidade entre dois pontos dados e P=(x,y) produz uma equação ax+by+c=0, com (a,b)≠(0,0).', ['coordinate-collinearity'], ['reta', 'equação geral', 'modelagem']),
  campaignSkill('line-solution-set', 'Reta como conjunto solução', 'definition', 'line-forge', 'A reta ax+by+c=0 é o conjunto dos pares (x,y) que satisfazem simultaneamente essa relação.', ['general-line-equation'], ['reta', 'conjunto solução', 'pertencimento']),
  campaignSkill('vertical-horizontal-lines', 'Retas verticais e horizontais', 'property', 'line-forge', 'x=k representa uma reta vertical e y=k representa uma reta horizontal, sem exigir coeficiente angular.', ['line-solution-set'], ['reta vertical', 'reta horizontal']),
  campaignSkill('supporting-line', 'Reta suporte', 'definition', 'line-forge', 'A reta suporte de um segmento contém suas extremidades e se prolonga indefinidamente nas duas direções.', ['general-line-equation', 'segments'], ['reta suporte', 'segmento']),
  campaignSkill('linear-system-classification', 'Classificação SPD, SI e SPI', 'algebra', 'line-forge', 'Duas equações lineares formam um sistema possível determinado, impossível ou possível indeterminado conforme tenham uma, nenhuma ou infinitas soluções.', ['line-solution-set'], ['sistema', 'SPD', 'SI', 'SPI']),
  campaignSkill('system-intersection-interpretation', 'Sistema e interseção', 'theorem', 'line-forge', 'As soluções comuns de duas equações são exatamente os pontos da interseção das retas que elas representam.', ['linear-system-classification'], ['sistema', 'interseção', 'posição relativa']),
  campaignSkill('figure-to-equation', 'Modelagem figura→equação', 'technique', 'line-forge', 'Uma figura é modelada identificando dados, pontos auxiliares e retas relevantes antes de escrever equações.', ['coordinate-midpoint', 'general-line-equation'], ['modelagem', 'figura', 'reta']),
  campaignSkill('exact-distance-proof', 'Prova métrica exata', 'technique', 'analytic-proofs', 'Distâncias por coordenadas devem permanecer exatas quando a tese exige uma igualdade, preservando radicais simplificados.', ['distance-formula-skill', 'figure-to-equation'], ['distância', 'radicais', 'prova']),
  campaignSkill('line-intersection', 'Interseção de retas e funções', 'algebra', 'analytic-proofs', 'Pontos de interseção satisfazem simultaneamente as equações das curvas.', ['coordinate-collinearity'], ['interseção', 'sistema']),
  campaignSkill('square-coordinate-properties', 'Quadrado por coordenadas', 'theorem', 'analytic-proofs', 'Distâncias, perpendicularidade e pontos médios caracterizam um quadrado no plano.', ['squared-distance', 'coordinate-midpoint'], ['quadrado', 'coordenadas']),
  campaignSkill('squared-distance-optimization', 'Otimização métrica quadrática', 'algebra', 'analytic-proofs', 'Somas de distâncias ao quadrado podem ser expandidas e completadas em quadrados em torno do baricentro.', ['squared-distance', 'centroid-coordinate'], ['otimização', 'quadrados']),
  campaignSkill('cartesian-inequalities', 'Inequações cartesianas', 'algebra', 'cartesian-plane', 'Uma inequação em x e y descreve uma região do plano limitada por uma curva de igualdade.', ['cartesian-coordinates'], ['inequação', 'região']),
  campaignSkill('inequality-systems', 'Sistemas de inequações', 'algebra', 'cartesian-plane', 'A solução de um sistema é a interseção das regiões determinadas por cada inequação.', ['cartesian-inequalities'], ['inequação', 'interseção']),
  campaignSkill('periodic-patterns', 'Padrões periódicos', 'algebra', 'analytic-proofs', 'Deslocamentos repetidos podem ser descritos por período e comprimento acumulado.', ['cartesian-coordinates'], ['periodicidade', 'padrão']),
  campaignSkill('coordinate-locus', 'Lugar geométrico coordenado', 'theorem', 'analytic-proofs', 'Um conjunto de condições métricas ou algébricas determina um lugar geométrico no plano.', ['coordinate-equidistance'], ['lugar geométrico', 'mediatriz']),
  campaignSkill('coordinate-equilateral', 'Equilátero por distâncias', 'theorem', 'analytic-proofs', 'Três pontos formam um triângulo equilátero quando as três distâncias ao quadrado são iguais e não nulas.', ['squared-distance', 'equilateral-triangle'], ['equilátero', 'distância']),
  campaignSkill('radical-perimeter', 'Perímetro com radicais', 'algebra', 'distances', 'Comprimentos radicais podem ser simplificados e somados preservando unidades e exatidão.', ['distance-formula-skill', 'triangle-perimeter'], ['radicais', 'perímetro']),
  campaignSkill('absolute-value-geometry', 'Módulo como distância', 'theorem', 'distances', 'O valor absoluto |x-a| representa a distância entre x e a na reta real.', ['distance-formula-skill'], ['módulo', 'distância']),
  campaignSkill('coordinate-proof', 'Prova por coordenadas', 'technique', 'analytic-proofs', 'Uma prova coordenada traduz hipóteses geométricas em identidades algébricas invariantes.', ['squared-distance', 'coordinate-collinearity'], ['prova', 'coordenadas']),
  campaignSkill('apollonius-identity', 'Identidade de Apolônio', 'theorem', 'analytic-proofs', 'A soma dos quadrados de dois lados relaciona-se ao quadrado da mediana e da metade da base.', ['coordinate-midpoint', 'squared-distance', 'coordinate-proof'], ['Apolônio', 'identidade métrica']),
];

export const skills: Skill[] = skillSeeds.map((seed) => ({
  ...seed,
  masteryDimensions: seed.masteryDimensions ?? [...allMasteryDimensions],
  unlocks: skillSeeds
    .filter((candidate) => candidate.prerequisites.includes(seed.id))
    .map((candidate) => candidate.id),
}));

export const regions: Region[] = [
  ['foundations', 'Fundamentos', 'A linguagem antes da prova', ['segments', 'midpoint']],
  ['angle-temple', 'Templo dos Ângulos', 'Relações que não dependem da medida', ['angles', 'complementary-angles', 'supplementary-angles', 'opv', 'angle-bisector']],
  ['parallel-pass', 'Passagem das Paralelas', 'Famílias angulares e suas conversas', ['parallel-angle-families', 'parallel-converse-skill']],
  ['quadrilaterals-city', 'Cidade dos Quadriláteros', 'Diagonais que revelam paralelogramos', ['parallelogram-characterization']],
  ['triangles', 'Triângulos', 'Correspondências começam nos vértices', ['triangles']],
  ['congruence-fortress', 'Fortaleza da Congruência', 'Cada igualdade precisa de fundamento', ['triangle-congruence', 'sas', 'isosceles-theorem', 'asa', 'isosceles-converse', 'sss']],
  ['cevians-sanctuary', 'Santuário das Cevianas', 'Uma construção, várias consequências', ['median', 'altitude', 'cevians', 'isosceles-special-cevian']],
  ['inequalities', 'Desigualdades', 'Comparar antes de calcular', []],
  ['cartesian-plane', 'Plano Cartesiano', 'A geometria ganha coordenadas', []],
  ['distances', 'Distâncias', 'Medidas deduzidas por relações', []],
  ['symmetries', 'Simetrias', 'Invariantes sob transformações', []],
  ['centers', 'Centros e Pontos Notáveis', 'Encontros inevitáveis de cevianas', []],
  ['analytic-proofs', 'Provas Analíticas', 'Argumentos geométricos em linguagem algébrica', []],
  ['line-forge', 'Forja das Retas', 'Da figura ao sistema, sem começar por slope', ['general-line-equation', 'line-solution-set', 'vertical-horizontal-lines', 'supporting-line', 'linear-system-classification', 'system-intersection-interpretation', 'figure-to-equation']],
].map(([id, title, subtitle, skillIds], index) => ({
  id: id as string,
  title: title as string,
  subtitle: subtitle as string,
  description: subtitle as string,
  skillIds: skillIds as string[],
  encounterIds: id === 'congruence-fortress' ? ['crossroads-opv', 'ordered-correspondence'] : [],
  accent: ['#b7f36b', '#67e8f9', '#fb923c', '#c084fc', '#facc15'][index % 5] ?? '#67e8f9',
  visibility: index < 3 ? 'visibleButLocked' : 'hiddenUntilDiscovered',
}));

export const encounters: Encounter[] = [
  {
    id: 'crossroads-opv',
    regionId: 'congruence-fortress',
    title: 'A Encruzilhada dos Triângulos',
    subtitle: 'Encounter piloto · OPV + LAL',
    kind: 'investigation',
    source: { origin: 'Lista Euclidiana', reference: 'Congruência com retas concorrentes' },
    sourceQuestion: 'Prove que △AFB e △HFR são congruentes usando apenas as marcas e as retas que se cruzam em F.',
    difficulty: 2,
    requires: ['angles', 'triangle-congruence'],
    teaches: ['opv', 'sas'],
    reinforces: ['triangle-congruence'],
    diagnosticTags: ['object-identification', 'relation-selection', 'justification-choice'],
    completionRules: {
      requiredStepIds: ['apply-opv', 'choose-sas', 'extract-consequence'],
      minimumCorrectSteps: 3,
      allowHints: true,
    },
    recoveryEncounters: [],
    briefing: 'Dois pares de lados já são congruentes. Encontre a relação angular que completa o argumento e escolha o critério.',
    objective: 'Provar △AFB ≅ △HFR por OPV e LAL.',
    assetKey: 'encounter-quest',
    figureKind: 'crossed-triangles',
    inventorySkillIds: ['opv', 'sas', 'cpctc'],
    initialRelationIds: ['given-af-fh', 'given-bf-fr'],
    hints: [
      'Comece pelo ponto F, onde as duas retas se cruzam.',
      '∠AFB e ∠HFR usam semirretas opostas duas a duas.',
      'Depois de registrar a congruência angular, conte: lado, ângulo compreendido, lado.',
    ],
    completionRelationIds: ['relation-ab-hr'],
    resolution: 'AF ≅ FH e BF ≅ FR são dados. Como ∠AFB e ∠HFR são OPV, eles são congruentes. Logo, △AFB ≅ △HFR por LAL e, por partes correspondentes, AB ≅ HR.',
    debrief: 'O desenho sugeria simetria, mas a prova dependeu de uma relação angular justificada e da escolha explícita do critério LAL.',
    unlockSkillIds: ['opv', 'sas', 'cpctc'],
    objects: [
      { id: 'point-a', kind: 'point', label: 'A' },
      { id: 'point-f', kind: 'point', label: 'F' },
      { id: 'point-b', kind: 'point', label: 'B' },
      { id: 'point-h', kind: 'point', label: 'H' },
      { id: 'point-r', kind: 'point', label: 'R' },
      { id: 'segment-af', kind: 'segment', label: 'AF' },
      { id: 'segment-fh', kind: 'segment', label: 'FH' },
      { id: 'segment-bf', kind: 'segment', label: 'BF' },
      { id: 'segment-fr', kind: 'segment', label: 'FR' },
      { id: 'segment-ab', kind: 'segment', label: 'AB' },
      { id: 'segment-hr', kind: 'segment', label: 'HR' },
      { id: 'angle-afb', kind: 'angle', label: '∠AFB' },
      { id: 'angle-hfr', kind: 'angle', label: '∠HFR' },
      { id: 'triangle-afb', kind: 'triangle', label: '△AFB' },
      { id: 'triangle-hfr', kind: 'triangle', label: '△HFR' },
    ],
    relations: [
      { id: 'given-af-fh', kind: 'congruent', objectIds: ['segment-af', 'segment-fh'], notation: 'AF ≅ FH', reason: 'Hipótese' },
      { id: 'given-bf-fr', kind: 'congruent', objectIds: ['segment-bf', 'segment-fr'], notation: 'BF ≅ FR', reason: 'Hipótese' },
      { id: 'relation-opv', kind: 'opposite-vertical', objectIds: ['angle-afb', 'angle-hfr'], notation: '∠AFB ≅ ∠HFR', reason: 'OPV' },
      { id: 'relation-triangles-sas', kind: 'congruent', objectIds: ['triangle-afb', 'triangle-hfr'], notation: '△AFB ≅ △HFR', reason: 'LAL' },
      { id: 'relation-ab-hr', kind: 'congruent', objectIds: ['segment-ab', 'segment-hr'], notation: 'AB ≅ HR', reason: 'Partes correspondentes' },
    ],
    justifications: [
      { id: 'justify-opv', label: 'Opostos pelo vértice', description: 'Os lados dos ângulos são semirretas opostas duas a duas.', skillId: 'opv' },
      { id: 'justify-sas', label: 'Lado–Ângulo–Lado', description: 'O ângulo congruente está compreendido entre os dois pares de lados.', skillId: 'sas' },
      { id: 'justify-cpctc', label: 'Partes correspondentes', description: 'Lados correspondentes de triângulos congruentes são congruentes.', skillId: 'cpctc' },
    ],
    steps: [
      {
        id: 'apply-opv',
        kind: 'justify',
        prompt: 'Aplique OPV ao par de ângulos correto.',
        objectIds: ['angle-afb', 'angle-hfr'],
        expectedIds: ['angle-afb', 'angle-hfr'],
      },
      {
        id: 'choose-sas',
        kind: 'justify',
        prompt: 'Escolha o critério e os triângulos que ele relaciona.',
        objectIds: ['triangle-afb', 'triangle-hfr'],
        expectedIds: ['triangle-afb', 'triangle-hfr'],
      },
      {
        id: 'extract-consequence',
        kind: 'justify',
        prompt: 'Extraia uma consequência da congruência provada.',
        objectIds: ['segment-ab', 'segment-hr'],
        expectedIds: ['segment-ab', 'segment-hr'],
      },
    ],
    applicationRules: [
      {
        id: 'rule-opv',
        skillId: 'opv',
        objectIds: ['angle-afb', 'angle-hfr'],
        requiresRelationIds: [],
        producesRelationIds: ['relation-opv'],
        successMessage: 'Relação registrada: ∠AFB ≅ ∠HFR por OPV.',
        semanticErrors: [
          { when: 'wrong-skill', message: 'Essa relação nasce do cruzamento de duas retas. Procure uma skill angular.' },
          { when: 'wrong-objects', message: 'Esses ângulos não são OPV: os lados precisam ser semirretas opostas duas a duas.' },
          { when: 'wrong-order', message: 'A ordem dos ângulos não é o obstáculo aqui; confira quais semirretas formam cada um.' },
          { when: 'missing-relation', message: 'Ainda falta uma relação anterior para aplicar essa skill.' },
        ],
      },
      {
        id: 'rule-sas',
        skillId: 'sas',
        objectIds: ['triangle-afb', 'triangle-hfr'],
        requiresRelationIds: ['given-af-fh', 'given-bf-fr', 'relation-opv'],
        producesRelationIds: ['relation-triangles-sas'],
        successMessage: 'Critério confirmado: △AFB ≅ △HFR por LAL.',
        semanticErrors: [
          { when: 'wrong-skill', message: 'As relações disponíveis formam lado–ângulo compreendido–lado. Nomeie esse critério.' },
          { when: 'wrong-objects', message: 'Aplique o critério aos dois triângulos completos, não a lados isolados.' },
          { when: 'wrong-order', message: 'A correspondência entre os triângulos está invertida.' },
          { when: 'missing-relation', message: 'Falta justificar a etapa intermediária: prove primeiro a congruência dos ângulos em F.' },
        ],
      },
      {
        id: 'rule-cpctc',
        skillId: 'cpctc',
        objectIds: ['segment-ab', 'segment-hr'],
        requiresRelationIds: ['relation-triangles-sas'],
        producesRelationIds: ['relation-ab-hr'],
        successMessage: 'Consequência registrada: AB ≅ HR por partes correspondentes.',
        semanticErrors: [
          { when: 'wrong-skill', message: 'A congruência já foi provada. Agora use a propriedade que transfere igualdade às partes correspondentes.' },
          { when: 'wrong-objects', message: 'A correspondência correta é A↔H e B↔R; selecione os lados formados por esses pares.' },
          { when: 'wrong-order', message: 'Leia a ordem △AFB ≅ △HFR para alinhar as extremidades.' },
          { when: 'missing-relation', message: 'Primeiro é necessário provar a congruência completa dos triângulos.' },
        ],
      },
    ],
  },
  {
    id: 'ordered-correspondence',
    regionId: 'congruence-fortress',
    title: 'A Ordem dos Vértices',
    subtitle: 'Encounter 02 · correspondência',
    kind: 'application',
    source: { origin: 'Complemento', reference: 'Correspondência ordenada em congruência' },
    sourceQuestion: 'Se △ABC ≅ △DEF, quais vértices correspondem na ordem escrita?',
    difficulty: 1,
    requires: ['triangle-congruence'],
    teaches: [],
    reinforces: ['triangle-congruence'],
    diagnosticTags: ['object-identification', 'relation-selection'],
    completionRules: { requiredStepIds: ['order-correspondence', 'side-correspondence', 'angle-correspondence'], minimumCorrectSteps: 3, allowHints: true },
    recoveryEncounters: [],
    briefing: 'A ordem dos nomes carrega a correspondência. Construa os três pares sem trocar vértices.',
    objective: 'Registrar A↔D, B↔E e C↔F na ordem correta.',
    assetKey: 'encounter-quest',
    figureKind: 'ordered-triangles',
    inventorySkillIds: ['triangle-congruence'],
    initialRelationIds: [],
    hints: ['Leia as duas palavras triangulares da esquerda para a direita.', 'O primeiro vértice de △ABC corresponde ao primeiro de △DEF.', 'Selecione A, D, B, E, C, F nessa sequência.'],
    completionRelationIds: ['relation-angle-correspondence'],
    resolution: 'Na escrita △ABC ≅ △DEF, as posições determinam A↔D, B↔E e C↔F; daí AB↔DE, BC↔EF, AC↔DF e ∠A↔∠D, ∠B↔∠E, ∠C↔∠F.',
    debrief: 'A mesma ordem governa vértices, lados e ângulos. Não é uma convenção visual: é parte da afirmação de congruência.',
    unlockSkillIds: [],
    objects: [
      { id: 'vertex-a', kind: 'point', label: 'A' }, { id: 'vertex-b', kind: 'point', label: 'B' }, { id: 'vertex-c', kind: 'point', label: 'C' },
      { id: 'vertex-d', kind: 'point', label: 'D' }, { id: 'vertex-e', kind: 'point', label: 'E' }, { id: 'vertex-f', kind: 'point', label: 'F' },
      { id: 'side-ab', kind: 'segment', label: 'AB' }, { id: 'side-de', kind: 'segment', label: 'DE' },
      { id: 'side-bc', kind: 'segment', label: 'BC' }, { id: 'side-ef', kind: 'segment', label: 'EF' },
      { id: 'side-ac', kind: 'segment', label: 'AC' }, { id: 'side-df', kind: 'segment', label: 'DF' },
      { id: 'angle-a', kind: 'angle', label: '∠A' }, { id: 'angle-d', kind: 'angle', label: '∠D' },
      { id: 'angle-b', kind: 'angle', label: '∠B' }, { id: 'angle-e', kind: 'angle', label: '∠E' },
      { id: 'angle-c', kind: 'angle', label: '∠C' }, { id: 'angle-f', kind: 'angle', label: '∠F' },
      { id: 'triangle-abc', kind: 'triangle', label: '△ABC' }, { id: 'triangle-def', kind: 'triangle', label: '△DEF' },
    ],
    relations: [
      { id: 'relation-correspondence', kind: 'correspondence', objectIds: ['vertex-a', 'vertex-d', 'vertex-b', 'vertex-e', 'vertex-c', 'vertex-f'], notation: 'A↔D · B↔E · C↔F', reason: 'Ordem da congruência' },
      { id: 'relation-side-correspondence', kind: 'correspondence', objectIds: ['side-ab', 'side-de', 'side-bc', 'side-ef', 'side-ac', 'side-df'], notation: 'AB↔DE · BC↔EF · AC↔DF', reason: 'Vértices correspondentes' },
      { id: 'relation-angle-correspondence', kind: 'correspondence', objectIds: ['angle-a', 'angle-d', 'angle-b', 'angle-e', 'angle-c', 'angle-f'], notation: '∠A↔∠D · ∠B↔∠E · ∠C↔∠F', reason: 'Vértices correspondentes' },
    ],
    justifications: [{ id: 'justify-order', label: 'Ordem escrita', description: 'Vértices na mesma posição dos nomes dos triângulos são correspondentes.', skillId: 'triangle-congruence' }],
    steps: [
      { id: 'order-correspondence', kind: 'select-object', prompt: 'Selecione os vértices em pares correspondentes.', objectIds: ['vertex-a', 'vertex-d', 'vertex-b', 'vertex-e', 'vertex-c', 'vertex-f'], expectedIds: ['vertex-a', 'vertex-d', 'vertex-b', 'vertex-e', 'vertex-c', 'vertex-f'] },
      { id: 'side-correspondence', kind: 'select-object', prompt: 'Mapeie os lados na ordem induzida pelos vértices.', objectIds: ['side-ab', 'side-de', 'side-bc', 'side-ef', 'side-ac', 'side-df'], expectedIds: ['side-ab', 'side-de', 'side-bc', 'side-ef', 'side-ac', 'side-df'] },
      { id: 'angle-correspondence', kind: 'select-object', prompt: 'Finalize mapeando os ângulos correspondentes.', objectIds: ['angle-a', 'angle-d', 'angle-b', 'angle-e', 'angle-c', 'angle-f'], expectedIds: ['angle-a', 'angle-d', 'angle-b', 'angle-e', 'angle-c', 'angle-f'] },
    ],
    applicationRules: [
      {
        id: 'rule-correspondence',
        skillId: 'triangle-congruence',
        objectIds: ['vertex-a', 'vertex-d', 'vertex-b', 'vertex-e', 'vertex-c', 'vertex-f'],
        orderMatters: true,
        requiresRelationIds: [],
        producesRelationIds: ['relation-correspondence'],
        successMessage: 'Correspondência registrada: A↔D, B↔E e C↔F.',
        semanticErrors: [
          { when: 'wrong-skill', message: 'Use a definição de congruência para preservar a correspondência dos vértices.' },
          { when: 'wrong-objects', message: 'Forme três pares usando todos os vértices dos dois triângulos.' },
          { when: 'wrong-order', message: 'A correspondência está invertida. Compare a posição de cada letra nos nomes △ABC e △DEF.' },
          { when: 'missing-relation', message: 'Falta registrar uma relação necessária antes desta aplicação.' },
        ],
      },
      {
        id: 'rule-side-correspondence',
        skillId: 'triangle-congruence',
        objectIds: ['side-ab', 'side-de', 'side-bc', 'side-ef', 'side-ac', 'side-df'],
        orderMatters: true,
        requiresRelationIds: ['relation-correspondence'],
        producesRelationIds: ['relation-side-correspondence'],
        successMessage: 'Lados mapeados: AB↔DE, BC↔EF e AC↔DF.',
        semanticErrors: [
          { when: 'wrong-skill', message: 'Continue usando a correspondência da congruência para transportar os pares de vértices aos lados.' },
          { when: 'wrong-objects', message: 'Cada lado precisa ser pareado com o lado formado pelos vértices correspondentes.' },
          { when: 'wrong-order', message: 'A correspondência de um lado preserva suas duas extremidades; revise os pares de vértices.' },
          { when: 'missing-relation', message: 'Registre primeiro a correspondência dos vértices.' },
        ],
      },
      {
        id: 'rule-angle-correspondence',
        skillId: 'triangle-congruence',
        objectIds: ['angle-a', 'angle-d', 'angle-b', 'angle-e', 'angle-c', 'angle-f'],
        orderMatters: true,
        requiresRelationIds: ['relation-side-correspondence'],
        producesRelationIds: ['relation-angle-correspondence'],
        successMessage: 'Ângulos mapeados: ∠A↔∠D, ∠B↔∠E e ∠C↔∠F.',
        semanticErrors: [
          { when: 'wrong-skill', message: 'Use a mesma correspondência ordenada; agora aplique-a aos ângulos.' },
          { when: 'wrong-objects', message: 'Selecione os seis ângulos, formando três pares.' },
          { when: 'wrong-order', message: 'O vértice que nomeia o ângulo precisa ocupar a mesma posição nos nomes dos triângulos.' },
          { when: 'missing-relation', message: 'Complete antes o mapeamento dos lados.' },
        ],
      },
    ],
  },
];

const formulas: Partial<Record<string, string>> = {
  opv: String.raw`\angle A\hat{O}C \cong \angle B\hat{O}D`,
  sas: String.raw`AB \cong DE,\; \angle B \cong \angle E,\; BC \cong EF`,
  asa: String.raw`\angle A \cong \angle D,\; AB \cong DE,\; \angle B \cong \angle E`,
  sss: String.raw`AB \cong DE,\; BC \cong EF,\; AC \cong DF`,
};

export const codexEntries: CodexEntry[] = skills.map((skill) => ({
  id: skill.codexEntryId,
  skillId: skill.id,
  title: skill.title,
  summary: skill.description,
  statement: skill.formalStatement,
  ...(formulas[skill.id] ? { formula: formulas[skill.id] } : {}),
  unlockedByDefault: skill.prerequisites.length === 0,
  sourceRefs: skill.sourceRefs,
}));

assertValidContent({ skills, regions, encounters, codexEntries, assetManifest: assetMap });

export function findEncounter(id: string) {
  return encounters.find((encounter) => encounter.id === id);
}

export function findCodexEntry(id: string) {
  return codexEntries.find((entry) => entry.id === id);
}
